import {
  getAllAuthorWithSlug,
  getAuthorPostsForSlugLimited,
} from "../../../lib/api";
import { GetStaticPaths, GetStaticProps } from "next";
import Link from "next/link";
import Image from "next/image";
import Layout from "../../../components/layout";
import "../../../styles/scss/blog.module.scss";
import parse from "html-react-parser";
import dayjs from "dayjs";
import { mwImage, stripImageSizeSuffix } from "../../../lib/common";
import { serverSideTranslations } from "next-i18next/serverSideTranslations";
import nextI18NextConfig from "../../../next-i18next.config";

export default function Author({ posts }) {
  let postData = posts?.users?.edges?.[0]?.node.posts.nodes;
  let authorName = posts?.users?.edges?.[0]?.node.name;

  return (
    <>
      <Layout
        isHomePage={false}
        pageTitle={authorName}
        ogImage={null}
        uri={`/blog/authors/${posts?.posts?.edges?.[0]?.node?.author.node.slug}/`}
        metaTitle={authorName}
        metaDesc={authorName}
        metaRobotsNoindex={"follow"}
        metaRobotsNofollow={"index"}
      >
        <div className={"middle-wrapper site-page author-page"}>
          <div className={"hero-blank"}></div>
          <div className={"container"}>
            <div className={"author-hero"}>
              <div className={"d-flex"}>
                <div className={"avtar"}>
                  <Image
                    src={mwImage(stripImageSizeSuffix(posts?.users?.edges?.[0]?.node?.avatar?.url))}
                    alt={authorName}
                    width={96}
                    height={96}
                    loading="lazy"
                    unoptimized
                  />
                </div>
                <div className={"avtar-description"}>
                  <h1 className={"title"}>{authorName}</h1>
                  {posts?.users?.edges?.[0]?.node.description !== null ? (
                    <p className={"description"}>
                      {posts?.users?.edges?.[0]?.node.description}
                    </p>
                  ) : (
                    ""
                  )}
                </div>
              </div>
            </div>

            <div className="content-container">
              <div className={"author-blogs"}>
                <h3>{authorName}'s articles</h3>
                <div className={"d-flex"}>
                  {postData?.map((post, index) => {
                    const title = post.title;
                    let link = post.slug;
                    if (
                      post?.parentSlug?.parentSlug != null &&
                      post?.parentSlug?.parentSlug.length > 0
                    ) {
                      link = `${post?.parentSlug?.parentSlug}`;
                    }

                    return (
                      <div className={"inner-block"} key={index}>
                        <Link href={`/blog/${link}`} itemProp="url">
                          <div className={"image-wrapper"}>
                            <Image
                              src={mwImage(post?.featuredImage?.node?.sourceUrl)}
                              alt={title}
                              width={400}
                              height={250}
                              loading="lazy"
                              unoptimized
                              sizes="(max-width: 768px) 100vw, 400px"
                            />
                          </div>
                        </Link>
                        <div>
                          <Link
                            href={`/blog/category/${post.categories.nodes?.[0]?.slug}`}
                          >
                            <div
                              className={`category ${post.categories.nodes?.[0]?.slug}`}
                            >
                              {post.categories.nodes?.[0]?.name}
                            </div>
                          </Link>
                          <div key={link}>
                            <article
                              className="post-list-item"
                              itemScope
                              itemType="http://schema.org/Article"
                            >
                              <header>
                                <Link href={`/blog/${link}`} itemProp="url">
                                  <h4>{parse(title)}</h4>
                                </Link>
                                <small>
                                  {dayjs(post.date).format("MMM D, YYYY")}
                                </small>
                              </header>
                            </article>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
}

export const getStaticProps: GetStaticProps = async ({
  params,
  preview = false,
  previewData,
  locale,
}) => {
  const data = await getAuthorPostsForSlugLimited(params?.slug);
  let authorName = data?.users?.edges?.[0]?.node.name;
  return {
    props: {
      preview,
      posts: data,
      pageTitle: authorName,
      description: authorName,
      ogImage: null,
      uri: `/blog/authors/${data?.users?.edges?.[0]?.node.slug}/`,
      metaTitle: authorName,
      metaDesc: authorName,
      metaRobotsNoindex: "index",
      metaRobotsNofollow: "follow",
      ...(await serverSideTranslations(locale ?? 'en', ['common', 'header', 'footer'], nextI18NextConfig)),
    },
  };
};

export const getStaticPaths: GetStaticPaths = async ({ locales }) => {
  const allAuthors = await getAllAuthorWithSlug();

  const paths =
    allAuthors?.edges?.flatMap(({ node }) => {
      if (locales && locales.length > 0) {
        return locales.map((locale) => ({
          params: { slug: node.slug },
          locale,
        }));
      }

      return {
        params: { slug: node.slug },
      };
    }) || [];

  return {
    paths,
    fallback: false, // author slugs finite hain
  };
};

